import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { FlexModule } from '@angular/flex-layout/flex';
import { Observable } from 'rxjs';
import { ApiResponse, CreditFlowConsentBlockIndividual, ConsentBlockDispatchState, CONSENT_PENDING_SIGNATURE_LABEL } from '@portal-workspace/grow-shared-library';
import { PortalHotToastService } from '../portal-hot-toast-component/hot-toast.service';
import { MessageBoxComponent } from '../message-box/message-box.component';
import { PrivacyConsentStatusChipComponent } from '../privacy-consent-status-component/privacy-consent-status-chip.component';

export interface CreditConsentRequiredDialogData {
  individuals: CreditFlowConsentBlockIndividual[];
  // Sends/resends the privacy consent request for a signatory (returns the API response).
  resendFn: (individualId: string) => Observable<ApiResponse>;
}

interface ConsentRow extends CreditFlowConsentBlockIndividual {
  sending: boolean;
  sent: boolean;
}

@Component({
  templateUrl: './credit-consent-required.dialog.html',
  styleUrls: ['./credit-consent-required.dialog.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatTableModule,
    FlexModule,
    MessageBoxComponent,
    PrivacyConsentStatusChipComponent,
  ],
})
export class CreditConsentRequiredDialog implements OnInit {

  displayedColumns = ['name', 'status', 'action'];
  rows: ConsentRow[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CreditConsentRequiredDialogData,
    private dialogRef: MatDialogRef<CreditConsentRequiredDialog>,
    private toastService: PortalHotToastService,
  ) {}

  ngOnInit(): void {
    this.rows = (this.data.individuals ?? []).map(individual => ({ ...individual, sending: false, sent: false }));
  }

  actionLabel(row: ConsentRow): string {
    if (row.sent) {
      return 'Sent';
    }
    return row.dispatchState === 'pending' ? 'Resend' : 'Send';
  }

  // Maps the signatory's dispatch state to a stable chip style token so the status colour matches
  // the KYC tab's privacy-consent chips (blue = pending, orange = expired, red = declined/failed).
  statusStyleKey(row: ConsentRow): string {
    const styleKeyByDispatchState: Record<ConsentBlockDispatchState, string> = {
      none: 'superseded',
      pending: 'pending',
      expired: 'expired',
      declined: 'declined',
      failed: 'dispatchfailed',
    };
    return styleKeyByDispatchState[row.dispatchState] ?? 'superseded';
  }

  send(row: ConsentRow): void {
    if (row.sending || row.sent) {
      return;
    }
    row.sending = true;
    this.data.resendFn(row.individualId).pipe(
      this.toastService.spinnerObservable(),
    ).subscribe({
      next: (response) => {
        row.sending = false;
        if (response?.status) {
          row.sent = true;
          row.dispatchState = 'pending';
          row.consentStatusLabel = CONSENT_PENDING_SIGNATURE_LABEL;
          this.toastService.success(`Consent request sent to ${row.name}`, 'Privacy consent');
        } else {
          this.toastService.error(response?.message ?? 'Unable to send consent request', 'Privacy consent');
        }
      },
      error: () => {
        row.sending = false;
        this.toastService.error('Unable to send consent request', 'Privacy consent');
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
